import axios from "axios";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import BASE_URL from "../../assets/global/url";

// ─── API endpoints ───────────────────────────────────────────────────────────
const DEFICIT_PRODUCT_URL = `${BASE_URL}/inventoryReport/deficitProduct`;
const DEFICIT_TOTALS_URL = `${BASE_URL}/inventoryReport/deficitProduct/totals`;

// ─── Excel color palette ─────────────────────────────────────────────────────
const COLORS = {
  // Headers
  primaryHeader: "FF1F4E79", // dark navy
  secondaryHeader: "FF2E75B6", // medium blue
  headerFont: "FFFFFFFF", // white

  // Meta / info rows
  metaBg: "FFF2F7FC", // very light blue
  metaLabel: "FF1F4E79",

  // Section title
  titleBg: "FFDCE6F1",
  titleFont: "FF1F4E79",

  // Data rows
  siRowBg: "FFD6E4F7", // light blue  – Sales Invoice
  prodRowBg: "FFD9F0F5", // light teal  – Production

  // Totals row
  totalRowBg: "FFBDD7EE",

  // Accents
  deficitRed: "FFC00000",
  borderColor: "FFB8CCE4",
  altRow: "FFF7FBFF", // alternating row tint
};

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

const formatDisplayDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

/**
 * Sanitises a string so it can be used as an Excel sheet name.
 * Rules: max 31 chars; no \  /  :  *  ?  [  ]  characters.
 */
const sanitizeSheetName = (name = "") =>
  name
    .replace(/[\\/:*?[\]]/g, "_")
    .substring(0, 31)
    .trim();

// ─── Cell-styling utilities ───────────────────────────────────────────────────

const thinBorder = (argb = COLORS.borderColor) => ({
  style: "thin",
  color: { argb },
});

const allBorders = (argb) => ({
  top: thinBorder(argb),
  left: thinBorder(argb),
  bottom: thinBorder(argb),
  right: thinBorder(argb),
});

/**
 * Applies a solid fill, bold white font and centred alignment –
 * the standard look for every table header cell.
 */
const styleHeaderCell = (cell, bgArgb = COLORS.primaryHeader) => {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb } };
  cell.font = { bold: true, color: { argb: COLORS.headerFont }, size: 10 };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  cell.border = allBorders();
};

/**
 * Applies a data-cell style.
 * @param {object} options
 * @param {boolean}  options.bold
 * @param {boolean}  options.underline
 * @param {string}   options.color  - ARGB font color (optional)
 * @param {'left'|'center'|'right'} options.align
 * @param {string}   options.bgArgb - ARGB fill color (optional)
 * @param {number}   options.size
 */
const styleDataCell = (
  cell,
  {
    bold = false,
    underline = false,
    color,
    align = "left",
    bgArgb,
    size = 10,
  } = {},
) => {
  if (bgArgb) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bgArgb },
    };
  }
  cell.font = {
    bold,
    underline,
    size,
    ...(color && { color: { argb: color } }),
  };
  cell.alignment = { horizontal: align, vertical: "middle" };
  cell.border = allBorders();
};

// ─── API fetch helpers ────────────────────────────────────────────────────────

/**
 * Fetches every deficit-product row for the given category (no pagination cap).
 * Returns an array of row objects.
 */
const fetchAllDeficitProducts = async ({
  startDate,
  endDate,
  productCategory,
}) => {
  const { data } = await axios.get(DEFICIT_PRODUCT_URL, {
    params: {
      startDate,
      endDate,
      productCategory,
      searchText: "",
      filterColumn: "all",
      page: 1,
      limit: 99999,
    },
  });
  return data.data ?? [];
};

/**
 * Fetches the aggregate totals row for the overview footer.
 */
const fetchDeficitTotals = async ({ startDate, endDate, productCategory }) => {
  const { data } = await axios.get(DEFICIT_TOTALS_URL, {
    params: {
      startDate,
      endDate,
      productCategory,
      searchText: "",
      filterColumn: "all",
    },
  });
  return data ?? {};
};

/**
 * Fetches all detail rows for one product + one module ("Sales Invoice" | "Production").
 * Returns an array of row objects.
 */
const fetchProductDetailRows = async ({
  productId,
  startDate,
  endDate,
  moduleName,
}) => {
  const { data } = await axios.get(`${DEFICIT_PRODUCT_URL}/${productId}`, {
    params: {
      startDate,
      endDate,
      moduleName,
      warehouseId: "all",
      page: 1,
      limit: 99999,
    },
  });
  return data.data ?? [];
};

/**
 * Fetches SI and Production rows for a product in parallel,
 * then combines them with a leading "Module" discriminator.
 */
const fetchCombinedProductDetails = async ({
  productId,
  startDate,
  endDate,
}) => {
  const [siRows, prodRows] = await Promise.all([
    fetchProductDetailRows({
      productId,
      startDate,
      endDate,
      moduleName: "Sales Invoice",
    }),
    fetchProductDetailRows({
      productId,
      startDate,
      endDate,
      moduleName: "Production",
    }),
  ]);
  return { siRows, prodRows };
};

// ─── Sheet builders ───────────────────────────────────────────────────────────

/**
 * Builds Sheet 1 – Overview.
 *
 * Layout:
 *   Row  1  – Report title
 *   Row  2  – Date generated
 *   Row  3  – (spacer)
 *   Row  4  – "Accounting Period" label
 *   Row  5  – Start Date
 *   Row  6  – End Date
 *   Row  7  – (spacer)
 *   Row  8  – "Overview" section title
 *   Rows 9–10 – Two-row table header
 *   Row 11+  – Data rows
 *   Last row – Totals
 */
const buildOverviewSheet = (
  workbook,
  { startDate, endDate, productCategory, products, totals },
) => {
  const sheet = workbook.addWorksheet("Overview");

  // Column widths (A–H)
  sheet.columns = [
    { key: "A", width: 18 }, // Item Code
    { key: "B", width: 36 }, // Product Name
    { key: "C", width: 13 }, // UoM
    { key: "D", width: 20 }, // SI Transactions
    { key: "E", width: 22 }, // SI Deficit
    { key: "F", width: 20 }, // Prod Transactions
    { key: "G", width: 22 }, // Prod Deficit
    { key: "H", width: 24 }, // Overall Deficit
  ];

  // ── Row 1: Title ─────────────────────────────────────────────────────────
  sheet.mergeCells("A1:H1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Inventory Report – Deficit Stocks";
  titleCell.font = { bold: true, size: 16, color: { argb: COLORS.titleFont } };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.titleBg },
  };
  sheet.getRow(1).height = 32;

  // ── Row 2: Date Generated ────────────────────────────────────────────────
  sheet.mergeCells("A2:H2");
  const dateGenCell = sheet.getCell("A2");
  dateGenCell.value = `Date Generated: ${new Date().toLocaleDateString(
    "en-US",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    },
  )}`;
  dateGenCell.font = {
    italic: true,
    size: 10,
    color: { argb: COLORS.metaLabel },
  };
  dateGenCell.alignment = { horizontal: "center", vertical: "middle" };
  dateGenCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.metaBg },
  };
  sheet.getRow(2).height = 18;

  // ── Row 3: Spacer ────────────────────────────────────────────────────────
  sheet.getRow(3).height = 6;

  // ── Row 4: Accounting Period label ───────────────────────────────────────
  sheet.mergeCells("A4:H4");
  const periodLabelCell = sheet.getCell("A4");
  periodLabelCell.value = "Accounting Period";
  periodLabelCell.font = {
    bold: true,
    size: 11,
    color: { argb: COLORS.metaLabel },
  };
  periodLabelCell.alignment = { horizontal: "left", vertical: "middle" };
  periodLabelCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.metaBg },
  };
  sheet.getRow(4).height = 20;

  // ── Rows 5–6: Start / End date ───────────────────────────────────────────
  const metaRows = [
    { label: "Start Date:", value: formatDisplayDate(startDate), row: 5 },
    { label: "End Date:", value: formatDisplayDate(endDate), row: 6 },
  ];

  metaRows.forEach(({ label, value, row }) => {
    sheet.mergeCells(`A${row}:B${row}`);
    const labelCell = sheet.getCell(`A${row}`);
    labelCell.value = label;
    labelCell.font = { bold: true, size: 10 };
    labelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLORS.metaBg },
    };
    labelCell.alignment = { vertical: "middle" };

    sheet.mergeCells(`C${row}:H${row}`);
    const valueCell = sheet.getCell(`C${row}`);
    valueCell.value = value;
    valueCell.font = { size: 10 };
    valueCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLORS.metaBg },
    };
    valueCell.alignment = { vertical: "middle" };
    sheet.getRow(row).height = 18;
  });

  // ── Row 7: Spacer ────────────────────────────────────────────────────────
  sheet.getRow(7).height = 6;

  // ── Row 8: "Overview" section title ──────────────────────────────────────
  sheet.mergeCells("A8:H8");
  const overviewTitleCell = sheet.getCell("A8");
  overviewTitleCell.value = `Overview – ${productCategory}`;
  overviewTitleCell.font = {
    bold: true,
    size: 13,
    color: { argb: COLORS.titleFont },
  };
  overviewTitleCell.alignment = { horizontal: "left", vertical: "middle" };
  sheet.getRow(8).height = 22;

  // ── Rows 9–10: Two-row table header ──────────────────────────────────────
  sheet.getRow(9).height = 28;
  sheet.getRow(10).height = 22;

  // Rowspan-2 columns (merge rows 9 & 10)
  const spanCols = ["A", "B", "C", "H"];
  spanCols.forEach((col) => sheet.mergeCells(`${col}9:${col}10`));

  // Sales Invoice colspan
  sheet.mergeCells("D9:E9");
  // Production colspan
  sheet.mergeCells("F9:G9");

  // Set row-9 header values
  const row9Headers = {
    A: "Item Code",
    B: "Product Name",
    C: "Unit of\nMeasurement",
    D: "Sales Invoice",
    F: "Production",
    H: "Overall Deficit\nStocks",
  };
  Object.entries(row9Headers).forEach(([col, value]) => {
    const cell = sheet.getCell(`${col}9`);
    cell.value = value;
    const bg = ["D", "F"].includes(col)
      ? COLORS.secondaryHeader
      : COLORS.primaryHeader;
    styleHeaderCell(cell, bg);
  });

  // Set row-10 sub-headers (SI + Production sub-columns)
  const row10Headers = {
    D: "Transactions",
    E: "Deficit Stocks",
    F: "Transactions",
    G: "Deficit Stocks",
  };
  Object.entries(row10Headers).forEach(([col, value]) => {
    const cell = sheet.getCell(`${col}10`);
    cell.value = value;
    styleHeaderCell(cell, COLORS.secondaryHeader);
  });

  // ── Data rows ─────────────────────────────────────────────────────────────
  let rowIdx = 11;
  products.forEach((item, i) => {
    const row = sheet.getRow(rowIdx);
    row.height = 18;

    const bgArgb = i % 2 === 0 ? undefined : COLORS.altRow;

    const productName = item.product_name ?? "";
    const targetSheetName = sanitizeSheetName(
      productName || `Product_${item.product_id}`,
    );

    const colDefs = [
      {
        col: "A",
        value: item.product_code ?? "",
        opts: { bold: true, align: "center", bgArgb },
      },
      {
        col: "B",
        value: {
          text: productName,
          hyperlink: `#'${targetSheetName}'!A1`,
        },
        opts: {
          bgArgb,
          color: "FF0563C1",
          underline: true,
        },
      },
      {
        col: "C",
        value: item.unit_of_measure ?? "",
        opts: { align: "center", bgArgb },
      },
      {
        col: "D",
        value: Number(item.si_count) || 0,
        opts: { align: "center", bgArgb },
        numFmt: "#,##0",
      },
      {
        col: "E",
        value: Number(item.si_deficit) || 0,
        opts: { align: "center", color: COLORS.deficitRed, bgArgb },
        numFmt: "#,##0.00",
      },
      {
        col: "F",
        value: Number(item.prod_count) || 0,
        opts: { align: "center", bgArgb },
        numFmt: "#,##0",
      },
      {
        col: "G",
        value: Number(item.prod_deficit) || 0,
        opts: { align: "center", color: COLORS.deficitRed, bgArgb },
        numFmt: "#,##0.00",
      },
      {
        col: "H",
        value: Number(item.overall_deficit) || 0,
        opts: { align: "center", bold: true, color: COLORS.deficitRed, bgArgb },
        numFmt: "#,##0.00",
      },
    ];

    colDefs.forEach(({ col, value, opts, numFmt }) => {
      const cell = row.getCell(col);
      cell.value = value;
      styleDataCell(cell, opts);
      if (numFmt) cell.numFmt = numFmt;
    });

    rowIdx++;
  });

  // ── Totals row ─────────────────────────────────────────────────────────────
  if (totals) {
    sheet.mergeCells(`A${rowIdx}:C${rowIdx}`);
    const totalRow = sheet.getRow(rowIdx);
    totalRow.height = 22;

    const totalDefs = [
      {
        col: "A",
        value: "TOTAL",
        opts: {
          bold: true,
          align: "center",
          bgArgb: COLORS.totalRowBg,
          size: 11,
        },
      },
      {
        col: "D",
        value: Number(totals.total_si_count) || 0,
        opts: { bold: true, align: "center", bgArgb: COLORS.totalRowBg },
        numFmt: "#,##0",
      },
      {
        col: "E",
        value: Number(totals.total_si_deficit) || 0,
        opts: {
          bold: true,
          align: "center",
          color: COLORS.deficitRed,
          bgArgb: COLORS.totalRowBg,
        },
        numFmt: "#,##0.00",
      },
      {
        col: "F",
        value: Number(totals.total_prod_count) || 0,
        opts: { bold: true, align: "center", bgArgb: COLORS.totalRowBg },
        numFmt: "#,##0",
      },
      {
        col: "G",
        value: Number(totals.total_prod_deficit) || 0,
        opts: {
          bold: true,
          align: "center",
          color: COLORS.deficitRed,
          bgArgb: COLORS.totalRowBg,
        },
        numFmt: "#,##0.00",
      },
      {
        col: "H",
        value: Number(totals.total_overall_deficit) || 0,
        opts: {
          bold: true,
          align: "center",
          color: COLORS.deficitRed,
          bgArgb: COLORS.totalRowBg,
          size: 11,
        },
        numFmt: "#,##0.00",
      },
    ];

    totalDefs.forEach(({ col, value, opts, numFmt }) => {
      const cell = totalRow.getCell(col);
      cell.value = value;
      styleDataCell(cell, opts);
      if (numFmt) cell.numFmt = numFmt;
    });

    // Fill the merged B & C cells of the total row so borders render
    ["B", "C"].forEach((col) => {
      const cell = totalRow.getCell(col);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.totalRowBg },
      };
      cell.border = allBorders();
    });
  }

  // Freeze the header rows so they stay visible while scrolling
  sheet.views = [{ state: "frozen", ySplit: 10 }];

  return sheet;
};

/**
 * Builds one product-detail sheet (Sheet 2…N).
 *
 * Layout:
 *   Row 1  – Report title
 *   Row 2  – Item Code meta
 *   Row 3  – Product Name meta
 *   Row 4  – (spacer)
 *   Row 5  – Table header
 *   Row 6+ – Data rows (SI rows first, then Production rows)
 */
const buildProductDetailSheet = (workbook, { product, siRows, prodRows }) => {
  const sheetName = sanitizeSheetName(
    product.product_name ?? `Product_${product.product_id}`,
  );
  const sheet = workbook.addWorksheet(sheetName);

  // Column widths  (A=Module | B=TxnID | C=DR# | D=Deficit | E=Price | F=Date)
  sheet.columns = [
    { key: "A", width: 18 },
    { key: "B", width: 40 },
    { key: "C", width: 22 },
    { key: "D", width: 18 },
    { key: "E", width: 16 },
    { key: "F", width: 18 },
  ];

  // ── Row 1: Title ──────────────────────────────────────────────────────────
  sheet.mergeCells("A1:F1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Inventory Report – Deficit Stocks (Product Detail)";
  titleCell.font = { bold: true, size: 14, color: { argb: COLORS.titleFont } };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.titleBg },
  };
  sheet.getRow(1).height = 30;

  // ── Rows 2–3: Product meta ────────────────────────────────────────────────
  const metaRows = [
    { label: "Item Code:", value: product.product_code ?? "N/A", row: 2 },
    { label: "Product Name:", value: product.product_name ?? "N/A", row: 3 },
  ];

  metaRows.forEach(({ label, value, row }) => {
    sheet.mergeCells(`A${row}:B${row}`);
    const labelCell = sheet.getCell(`A${row}`);
    labelCell.value = label;
    labelCell.font = {
      bold: true,
      size: 10,
      color: { argb: COLORS.metaLabel },
    };
    labelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLORS.metaBg },
    };
    labelCell.alignment = { vertical: "middle" };

    sheet.mergeCells(`C${row}:F${row}`);
    const valueCell = sheet.getCell(`C${row}`);
    valueCell.value = value;
    valueCell.font = { size: 10 };
    valueCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLORS.metaBg },
    };
    valueCell.alignment = { vertical: "middle" };
    sheet.getRow(row).height = 18;
  });

  // ── Row 4: Spacer ──────────────────────────────────────────────────────────
  sheet.getRow(4).height = 6;

  // ── Row 5: Table header ────────────────────────────────────────────────────
  const HEADERS = [
    "Module",
    "Transaction ID",
    "DR Number",
    "Deficit Stock",
    "Price (₱)",
    "Date In",
  ];
  const headerRow = sheet.getRow(5);
  headerRow.height = 22;
  HEADERS.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    styleHeaderCell(cell);
  });

  // ── Data rows ──────────────────────────────────────────────────────────────
  let rowIdx = 6;

  /**
   * Appends a batch of rows for a given module.
   * @param {Array}  rows
   * @param {'Sales Invoice'|'Production'} moduleName
   */
  const appendRows = (rows, moduleName) => {
    const bgArgb =
      moduleName === "Sales Invoice" ? COLORS.siRowBg : COLORS.prodRowBg;

    rows.forEach((rowData) => {
      const row = sheet.getRow(rowIdx);
      row.height = 17;

      const isSI = moduleName === "Sales Invoice";

      const colDefs = [
        { value: moduleName, opts: { bold: true, bgArgb } },
        { value: rowData.transaction_number ?? "N/A", opts: { bgArgb } },
        {
          value: isSI ? (rowData.dr_number ?? "N/A") : "—",
          opts: { align: "center", bgArgb },
        },
        {
          value: Number(rowData.stock) || 0,
          numFmt: "#,##0.00",
          opts: {
            align: "center",
            color: COLORS.deficitRed,
            bold: true,
            bgArgb,
          },
        },
        {
          value: Number(rowData.price) || 0,
          numFmt: "#,##0.00",
          opts: { align: "center", bgArgb },
        },
        {
          value: formatDisplayDate(rowData.date_in),
          opts: { align: "center", bgArgb },
        },
      ];

      colDefs.forEach(({ value, opts, numFmt }, i) => {
        const cell = row.getCell(i + 1);
        cell.value = value;
        styleDataCell(cell, opts);
        if (numFmt) cell.numFmt = numFmt;
      });

      rowIdx++;
    });
  };

  appendRows(siRows, "Sales Invoice");
  appendRows(prodRows, "Production");

  // Empty-state row
  if (siRows.length === 0 && prodRows.length === 0) {
    sheet.mergeCells(`A6:F6`);
    const emptyCell = sheet.getCell("A6");
    emptyCell.value =
      "No deficit records found for this product in the selected period.";
    emptyCell.font = { italic: true, color: { argb: "FF808080" } };
    emptyCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(6).height = 20;
  }

  // Freeze header rows
  sheet.views = [{ state: "frozen", ySplit: 5 }];

  return sheet;
};

// ─── Main export function ─────────────────────────────────────────────────────

/**
 * Generates and downloads the Deficit Stocks Excel report.
 *
 * @param {object} params
 * @param {string} params.fileName       - Base filename (without .xlsx extension)
 * @param {string} params.startDate      - Cutoff start date
 * @param {string} params.endDate        - Cutoff end date
 * @param {string} params.productCategory - Active deficit sub-tab category
 */
const exportExcelDeficitStock = async ({
  fileName,
  startDate,
  endDate,
  productCategory,
}) => {
  // ── Fetch overview data (all rows + totals) ────────────────────────────
  const [products, totals] = await Promise.all([
    fetchAllDeficitProducts({ startDate, endDate, productCategory }),
    fetchDeficitTotals({ startDate, endDate, productCategory }),
  ]);

  // ── Fetch per-product details in controlled batches ────────────────────
  const BATCH_SIZE = 5; // keep parallel requests manageable
  const productDetails = [];

  for (let i = 0; i < products.length; i += BATCH_SIZE) {
    const batch = products.slice(i, i + BATCH_SIZE);

    const batchResults = await Promise.all(
      batch.map(async (product) => {
        const { siRows, prodRows } = await fetchCombinedProductDetails({
          productId: product.product_id,
          startDate,
          endDate,
        });
        return { product, siRows, prodRows };
      }),
    );

    productDetails.push(...batchResults);
  }

  // ── Build workbook ─────────────────────────────────────────────────────
  const workbook = new Workbook();
  workbook.creator = "Inventory Report System";
  workbook.created = new Date();

  // Sheet 1 – Overview
  buildOverviewSheet(workbook, {
    startDate,
    endDate,
    productCategory,
    products,
    totals,
  });

  // Sheets 2-N – one per product
  productDetails.forEach(({ product, siRows, prodRows }) => {
    buildProductDetailSheet(workbook, { product, siRows, prodRows });
  });

  // ── Serialise and trigger download ─────────────────────────────────────
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileName}.xlsx`);
};

export { exportExcelDeficitStock };
